import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { phoneConstant } from '_constants/otherConstants';
import { CalendlyInterface } from '_types/calendly.interface';

import { fetchAdvisorDetails } from 'store/actions/advisor';
import { fetchGetProfileInfo } from 'store/actions/profile';
import { fetchGetUpcomingSessions } from 'store/actions/sessions';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { advisorDetailsSelector } from 'store/selectors/advisor';
import { profileSboInfoSelector } from 'store/selectors/profile';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import { getCurrentTimeZone } from 'utils/moment';

import { useCalendlyEventListener } from 'react-calendly';

import sessionsService from '_services/sessionsService';
import Button from 'app/components/Button';
import PlayIcon from 'app/components/Icons/PlayIcon';
import PreviewVideoModal from 'app/components/UploadVideo/components/PreviewVideoModal';
import VideoPlayer from 'app/components/VideoPlayer';
import BookingModalFooter from './BookingModalFooter';
import BookingModalHeader from './BookingModalHeader';
import { stepsConfig } from './config';

import './style.scss';

interface BookingModalProps {
  advisorId: number;
  onClose: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({ advisorId, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const isPhone = useMediaQuery({ query: `(max-width: ${phoneConstant}px)` });

  const advisor = useAppSelector(advisorDetailsSelector);
  const sboInfo = useAppSelector(profileSboInfoSelector);

  const [step, setStep] = useState(0);
  const [calendlyLink, setCalendlyLink] = useState<string>('');
  const [bookingData, setBookingData] = useState<CalendlyInterface | null>(
    null,
  );
  const [isShowIntroMobile, setIsShowIntroMobile] = useState(false);

  useEffect(() => {
    dispatch(
      fetchAdvisorDetails({
        id: advisorId,
        timeZone: getCurrentTimeZone(),
      }),
    );
  }, [advisorId, dispatch]);

  useCalendlyEventListener({
    onEventScheduled: async (e) => {
      try {
        const { invitee } = e.data.payload;
        const result = await sessionsService.createNewEvent(
          {
            inviteeUri: invitee.uri,
            advisorId,
          },
          getCurrentTimeZone(),
        );
        if (!result) throw new Error('No result from createNewEvent');
        setBookingData(result);
        setStep(2);
      } catch (err: any) {
        throw new Error(
          err?.response?.data?.message ||
            err?.message ||
            t('advisor.booking_modal.generic_error'),
        );
      }
    },
  });

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      dispatch(fetchGetProfileInfo());
      if (sboInfo?.id) {
        dispatch(
          fetchGetUpcomingSessions({
            limit: 20,
            offset: 0,
            sboInfoId: sboInfo.id.toString(),
          }),
        );
      }
    }, 1000);
  }, [onClose, dispatch, sboInfo]);

  const goNext = useCallback(() => {
    if (step === stepsConfig.length - 1) {
      handleClose();
    } else {
      setStep((s) => Math.min(s + 1, stepsConfig.length - 1));
    }
  }, [step, setStep, handleClose]);

  const onShowIntroClick = useCallback(() => setIsShowIntroMobile(true), []);
  const onMobileVideoClose = useCallback(() => setIsShowIntroMobile(false), []);

  const stepProps = (() => {
    switch (step) {
      case 0:
        return {
          advisor,
          setCalendlyLink,
          goNext,
          isCFO: advisor?.isCFO || false,
          isPartOfSpecialSession: advisor?.isPartOfSpecialSession || false,
          isPhone,
        };
      case 1:
        return {
          calendlyLink,
          sboInfo,
          advisor,
          setBookingData,
          handleClose,
          isPhone,
        };
      case 2:
        return {
          bookingData,
          isPhone,
        };
      default:
        return {};
    }
  })();

  const StepComponent = stepsConfig[step].component;

  const { ref } = useOnClickOutside(true, handleClose);

  return (
    <div className="modal_wrapper">
      <div
        className={clsx('modal_block', { modal_calendly: step === 1 })}
        ref={ref}
      >
        {step === 1 ? (
          <div style={{ width: '100%', height: '100%' }}>
            <StepComponent {...stepProps} />
          </div>
        ) : (
          <>
            {advisor?.video &&
              (isPhone ? (
                <PreviewVideoModal
                  isOpen={isShowIntroMobile}
                  videoUrl={advisor.video}
                  onModalClose={onMobileVideoClose}
                />
              ) : (
                <div className="modal_video">
                  <VideoPlayer url={advisor.video} />
                </div>
              ))}
            <div className="modal_right_content">
              <BookingModalHeader
                firstName={advisor?.firstName || ''}
                lastName={advisor?.lastName || ''}
                photo={advisor?.photo}
                company={advisor?.advisoryOrgName}
                bio={advisor?.bio}
              />
              {advisor?.video && (
                <Button
                  type="btn"
                  btnLook="filled-gray"
                  value={t('common.watch_intro')}
                  onClick={onShowIntroClick}
                  size="large"
                  leftIcon={<PlayIcon />}
                  className="modal_watch_intro"
                />
              )}
              <div className="modal_body">
                <StepComponent {...stepProps} />
              </div>
              <BookingModalFooter
                active={!!calendlyLink || step === stepsConfig.length - 1}
                step={step + 1}
                totalSteps={stepsConfig.length}
                onCancelClick={handleClose}
                onNextClick={goNext}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
